<script lang="ts" setup>
import { useCart } from '~/composables/useCart';
import { useCarriers } from '~/composables/useCarriers';
import { getPsImage } from '~/composables/useCms';
import CheckoutPoints from './CheckoutPoints.vue';

const { query: shippingQuery, mutation: shippingMutation, currentCarrier, totalCarriers } = useCarriers();
const { query: pudosQuery, mutation: pudosMutation, isActive, currentPudo } = usePudos();

const { data: cart } = useCart();

const formatDistance = (distance: string) => {
  if (!distance) return '';
  const [value, unit] = distance.split(' ');
  const formattedValue = value?.endsWith('.0') ? value.split('.')[0] : value;
  return `${formattedValue} ${unit}`;
};

const showingSchedule = ref<any>(null);

const showSchedule = (point: any) => {
  showingSchedule.value = point;
};
</script>

<template>
  <div class="flex flex-col gap-4" v-if="!shippingQuery.isLoading && totalCarriers > 0">
    <!-- Shipping Methods Section -->
    <div class="space-y-4" :class="{ 'opacity-50': shippingMutation.isPending }">
      <div class="grid grid-cols-1 gap-2">
        <button
          v-for="shippingMethod in shippingQuery.data"
          class="group relative flex items-center gap-4 rounded-lg border p-4 transition-all focus:ring-2 focus:ring-primary/50"
          :class="{
            'bg-primary-50 border border-primary-500': shippingMethod?.selected,
            'hover:border-gray-300 hover:bg-gray-50/50 border-gray-200 bg-white': !shippingMethod?.selected,
          }"
          @click="shippingMutation.mutate({
            id_address: cart.id_address_delivery,
            id_carrier: shippingMethod.id,
          })"
        >
          <div
            class="flex size-12 items-center justify-center border rounded-sm shadow-xs bg-white"
          >
            <NuxtImg
              v-if="shippingMethod.logo"
              class="max-h-10 max-w-10 object-contain"
              :src="getPsImage(shippingMethod.logo)"
              format="webp"
              :alt="shippingMethod.name"
              loading="lazy"
            />
            <div v-else class="flex h-12 w-12 items-center justify-center rounded-md bg-gray-100"></div>
          </div>

          <div class="grid grid-cols-[1fr_auto] gap-2 justify-between w-full items-center" :id="`shipping-method-${shippingMethod.id}`">
            <div class="text-left w-full">
              <p class="font-medium text-gray-900">{{ shippingMethod.name }}</p>
              <p class="text-sm text-gray-500 wrap-break-word">{{ shippingMethod.label }}</p>
            </div>
            <span class="text-base text-right font-medium text-primary whitespace-nowrap justify-end">
              {{ shippingMethod.price }}
            </span>
          </div>
        </button>
      </div>
    </div>

    <!-- Pickup Points Section -->
    <div v-if="isActive" class="space-y-4">
      <CheckoutPoints />

      <div v-if="pudosQuery.isLoading" class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Skeleton v-for="i in 4" :key="i" class="h-32 rounded-lg" />
      </div>
      <div
        v-else-if="pudosQuery.data"
        class="grid grid-cols-1 gap-3 md:grid-cols-2 max-h-125 overflow-y-auto py-3 px-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
      >
        <div v-if="!pudosQuery.data.content?.pudoList?.length" class="col-span-full text-center py-6 text-gray-500">
          {{ $t('No pickup points available') }}
        </div>

        <div
          v-for="point in pudosQuery.data.content?.pudoList ?? []"
          :key="point.ID"
          class="group relative flex flex-col items-start rounded-lg border p-4 text-left transition-all focus:ring-2 focus:ring-primary/50"
          :class="{
            'border-primary bg-primary-50': currentPudo?.ID === point.ID,
            'hover:border-gray-300 hover:bg-gray-50/50 border-gray-200 bg-white': currentPudo?.ID !== point.ID,
            'opacity-75 pointer-events-none': pudosMutation.isPending,
          }"
        >
          <button
            @click="pudosMutation.mutate({
              id_address: cart.id_address_delivery,
              selected_pudo: point.ID,
              id_carrier: currentCarrier!.id,
              filter_range: '1-10'
            })"
            :disabled="pudosMutation.isPending"
            :aria-label="`Select pickup point ${point.name}`"
            class="w-full flex text-left flex-col"
          >
            <div class="flex w-full items-start justify-between">
              <div>
                <p class="font-medium text-gray-900">{{ point.name }} - {{ point.carrier }}</p>
                <p class="mt-1 text-sm text-gray-500">
                  <span class="block">{{ point.street }}</span>
                  <span class="block">{{ point.postCode }} {{ point.city }}</span>
                </p>
              </div>
              <div class="flex items-center">
                <svg
                  v-if="pudosMutation.isPending && currentPudo?.ID == point.ID"
                  class="animate-spin h-5 w-5 text-primary"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <svg
                  v-else
                  class="h-5 w-5 shrink-0 text-primary"
                  :class="{ invisible: currentPudo?.ID !== point.ID }"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>

            <div class="mt-4 flex w-full items-center justify-between">
              <div class="flex items-center gap-1.5 text-sm text-gray-600">
                <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{{ formatDistance(point.distanceFormatted) }}</span>
              </div>

              <div class="flex items-center gap-1.5 text-sm text-gray-600">
                <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span
                  v-if="
                    point.businessDays &&
                    point.businessDays.length > 0 &&
                    point.businessDays[0] &&
                    point.businessDays[0].businessHours
                  "
                >
                  {{ point.businessDays[0].businessHours.map((h) => `${h.open} - ${h.close}`).join(', ') }}
                </span>
                <span v-else>{{ $t('Hours not available') }}</span>
              </div>
            </div>
          </button>

          <div class="mt-2 w-full text-sm text-gray-600">
            <button
              @click.stop="showSchedule(point)"
              type="button"
              class="flex items-center gap-1 text-primary hover:text-primary/80 focus:outline-none focus:underline"
              :aria-label="`View full schedule for ${point.name}`"
            >
              <span>{{ $t('View full schedule') }}</span>
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- Schedule Dialog -->
  <Dialog :open="!!showingSchedule">
    <DialogContent
      class="max-h-[80vh] overflow-y-auto"
      @close-auto-focus="showingSchedule = null"
      @escape-key-down="showingSchedule = null"
      @close="showingSchedule = null"
      @pointer-down-outside="showingSchedule = null"
    >
      <DialogHeader>
        <DialogTitle class="capitalize">{{ showingSchedule?.name }}</DialogTitle>
      </DialogHeader>

      <div v-if="showingSchedule?.businessDays?.length" class="grid gap-2">
        <div v-for="day in showingSchedule.businessDays" :key="day.dayNameIT" class="flex flex-col gap-2 rounded-lg bg-gray-50 p-3">
          <span class="font-medium capitalize">{{ day.dayNameIT }}</span>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="(hours, index) in day.businessHours"
              :key="index"
              class="text-sm text-gray-600 bg-white px-2 py-1 rounded-md"
            >
              {{ hours.open }} - {{ hours.close }}
            </span>
            <span v-if="!day.businessHours || day.businessHours.length === 0" class="text-sm text-gray-500">
              {{ $t('Closed') }}
            </span>
          </div>
        </div>
      </div>
      <div v-else class="py-4 text-center text-gray-500">
        {{ $t('No schedule information available') }}
      </div>
    </DialogContent>
  </Dialog>

  <div v-if="shippingQuery.isLoading" class="flex flex-col gap-6">
    <Skeleton class="size-10 w-full" />
    <Skeleton class="size-10 w-full" />
    <Skeleton class="size-10 w-full" />
  </div>

  <div v-else-if="!shippingQuery.isLoading && totalCarriers == 0">
    <Alert variant="destructive">
      <AlertDescription>
        {{ $t('No shipping methods available') }}
      </AlertDescription>
    </Alert>
  </div>
</template>
